/**
 * Profitability Tab Component
 * Features: CSV/PDF export, configurable row count, drill-down links, contextual empty state
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend,
} from "recharts";
import { DollarSign } from "lucide-react";
import { 
  ChartCard, TableCard, CustomTooltip, StatusBadge, DataTable, 
  TableRow, TableCell, JobLinkCell, EmptyState, EMPTY_STATES 
} from "./shared";
import {
  CHART_COLORS, CHART_STYLE, formatCurrency, formatCompactCurrency,
  formatPercent, getValueColor, exportToCSV, DEFAULT_ROW_COUNT,
  generateAnalyticsPdfContent, generateTableHtml,
} from "../../lib/chartUtils";
import useExportDialog from "../../hooks/useExportDialog";

const ProfitabilityTab = ({ data }) => {
  const [rowCount, setRowCount] = useState(DEFAULT_ROW_COUNT);
  const { exportToPdf } = useExportDialog();

  if (!data?.length) {
    return (
      <EmptyState 
        icon={DollarSign}
        {...EMPTY_STATES.profitability}
      />
    );
  }

  const handleExportCSV = () => {
    exportToCSV(data, "job-profitability", 
      ["Job Name", "Client", "Status", "Estimate", "Actual", "Profit/Loss", "Margin %"],
      {
        "Job Name": "job_name",
        "Client": "client_name",
        "Status": "status",
        "Estimate": "total_estimate",
        "Actual": "total_actual",
        "Profit/Loss": "profit_loss",
        "Margin %": "margin_pct",
      }
    );
  };

  const handleExportPDF = () => {
    const tableRows = data.slice(0, rowCount).map(job => [
      job.job_name,
      job.client_name,
      job.status,
      formatCurrency(job.total_estimate),
      formatCurrency(job.total_actual),
      `<span class="${job.profit_loss >= 0 ? 'green' : 'red'}">${formatCurrency(job.profit_loss)}</span>`,
      `<span class="${job.margin_pct >= 0 ? 'green' : 'red'}">${formatPercent(job.margin_pct)}</span>`,
    ]);

    const tableHtml = generateTableHtml(
      ["Job", "Client", "Status", "Estimate", "Actual", "Profit/Loss", "Margin"],
      tableRows,
      ["left", "left", "left", "right", "right", "right", "right"]
    );

    const totals = data.reduce((acc, job) => ({
      estimate: acc.estimate + job.total_estimate,
      actual: acc.actual + job.total_actual,
      profit: acc.profit + job.profit_loss,
    }), { estimate: 0, actual: 0, profit: 0 });

    const summaryHtml = `
      <div style="display: flex; gap: 24px; margin-bottom: 16px;">
        <div><strong>Total Estimate:</strong> ${formatCurrency(totals.estimate)}</div>
        <div><strong>Total Actual:</strong> ${formatCurrency(totals.actual)}</div>
        <div><strong>Total Profit/Loss:</strong> <span class="${totals.profit >= 0 ? 'green' : 'red'}">${formatCurrency(totals.profit)}</span></div>
      </div>
    `;

    const content = generateAnalyticsPdfContent("Job Profitability Report", [
      { title: "Summary", content: summaryHtml },
      { title: "Job Details", content: tableHtml },
    ]);

    exportToPdf("Job Profitability Report", content);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estimate vs Actual Chart */}
        <ChartCard title="Estimate vs Actual by Job">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.slice(0, 8)} margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis dataKey="job_name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
              <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
              <Legend />
              <Bar dataKey="total_estimate" name="Estimate" fill={CHART_COLORS.info} radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_actual" name="Actual" fill={CHART_COLORS.warning} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Profit/Loss Chart */}
        <ChartCard title="Profit/Loss by Job">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.slice(0, 8)} margin={{ top: 20, right: 20, left: 20, bottom: 60 }}>
              <CartesianGrid {...CHART_STYLE.grid} />
              <XAxis dataKey="job_name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={formatCompactCurrency} tick={CHART_STYLE.axis} />
              <Tooltip content={<CustomTooltip formatter={formatCurrency} />} />
              <Bar dataKey="profit_loss" name="Profit/Loss" radius={[4, 4, 0, 0]}>
                {data.slice(0, 8).map((entry, index) => (
                  <Cell key={entry.job_id} fill={getValueColor(entry.profit_loss)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Detailed Table */}
      <TableCard 
        title="Job Profitability Details" 
        onExportCSV={handleExportCSV}
        onExportPDF={handleExportPDF}
        rowCount={rowCount}
        onRowCountChange={setRowCount}
      >
        <DataTable headers={[
          { label: "Job" },
          { label: "Status" },
          { label: "Estimate", align: "right" },
          { label: "Actual", align: "right" },
          { label: "Profit/Loss", align: "right" },
          { label: "Margin", align: "right" },
        ]}>
          {data.slice(0, rowCount).map((job) => (
            <TableRow key={job.job_id} className="table-row hover:bg-muted/50">
              <JobLinkCell jobId={job.job_id}>
                <div>
                  <p className="font-medium">{job.job_name}</p>
                  <p className="text-sm text-muted-foreground">{job.client_name}</p>
                </div>
              </JobLinkCell>
              <TableCell><StatusBadge status={job.status} /></TableCell>
              <TableCell className="text-right">{formatCurrency(job.total_estimate)}</TableCell>
              <TableCell className="text-right">{formatCurrency(job.total_actual)}</TableCell>
              <TableCell className={`text-right font-bold ${job.profit_loss >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatCurrency(job.profit_loss)}
              </TableCell>
              <TableCell className={`text-right ${job.margin_pct >= 0 ? "text-green-600" : "text-red-600"}`}>
                {formatPercent(job.margin_pct)}
              </TableCell>
            </TableRow>
          ))}
        </DataTable>
      </TableCard>
    </div>
  );
};

export default ProfitabilityTab;
